import {
	businessActivityIcon,
	classicReportIcon,
	companyIcon,
	compareIcon,
} from "../../core/elements/icons/index.js";
import { iconAndText } from "../../core/elements/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import { none } from "../../core/elements/size/index.js";
import accountingDocument from "../shared/accountingDocument.js";
import AnnualTrend from "../../models/accounting/AnnualTrend.js";
import BusinessOverviewWidget from "./BusinessOverviewWidget.js";
import companyOverviewWidget from "./companyOverviewWidget.js";
import CompanyTransitionBalance from "../../models/accounting/CompanyTransitionBalance.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import keyFigureOverviewWidget from "./keyFigureOverviewWidget.js";
import KeyFigurePresetService from "../../core/services/KeyFigurePresetService.js";
import KeyFigureResultService from "../../core/services/KeyFigureResultService.js";
import polyglot, { _ } from "../../core/polyglot/polyglot.js";
import session from "../../core/session.js";
import TopListsOverviewWidget from "../../core/widgets/TopListsOverviewWidget/TopListsOverviewWidget.js";
import TopListsService from "./TopListsService.js";

/**
 * Accounting's landing page that displays a sort of dashboard with an aggregated information overview with top five
 * profit, income and a summary of some of the most essential key figure information.
 *
 *  _Options:_
 *
 * @param {object} spec - Object configuration
 * @param {object} my - Protected properties
 */
function overviewDocument(spec, my) {
	my ||= {};
	const that = accountingDocument(spec, my);

	that.title = _("Overview");

	that.documentId = "overview";

	that.help = function () {
		return [
			{
				title: _("Overview"),
				content: _(
					"This page will give an overview to how the business is performing and the general financial position of the company. The first graph visualizes monthly profits and losses and how the accumulated result is developing. Another graph is visualizing assets and how they are financed. Furthermore KPIs and top lists are shown to give the business owner a good overview of the current position of the company.",
				),
			},
			{
				title: _("Business"),
				content: _(
					"The yellow bars visualize monthly profits and losses and the green line represents the accumulated result. By clicking on the yellow bars you will activate that specific period.",
				),
				element: "business-chart",
			},
			{
				title: _("Links to reports"),
				content: _(
					"Click the links below the chart to jump to one of the reports.",
				),
				element: "overview-quicklinks",
			},
			{
				title: _("The Company"),
				content: _(
					"This graph visualizes the company’s financial position. I.E the assets the company owns and how these are financed through equity capital and liabilities. Quick links will take you to pages that show you the same information but in a more detailed manner.",
				),
				element: "company-overview-chart",
			},
			{
				title: _("KPIs"),
				content: _(
					"Three KPIs are shown to give the user a better picture of how the company is doing.",
				),
				element: "kpi-overview",
			},
			{
				title: _("Top list Monthly Income"),
				content: _(
					"The top five months based on income is displayed as well as the current months ranking.",
				),
				element: "toplist",
			},
			{
				title: _("Top list Monthly Profit/Loss"),
				content: _(
					"The top five months based on profit/loss is displayed as well as the current months ranking.",
				),
				element: "toplist",
			},
		];
	};

	that.subtitle = function (html) {
		html.render(
			_("{company} - financial position at {period}", {
				company: session.getCompany().getName(),
				period: session.getPeriod().toSmartString(),
			}),
		);
	};

	const antiForgeryToken = session.getAntiForgeryToken();

	my.annualTrendRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/FiscalYear/#fiscalYearId/AnnualTrend",
		defaultFilters: { companyId: session.getCompany().getLegacyId() },
		model: AnnualTrend,
		antiForgeryToken,
	});

	my.keyFigureResultService = new KeyFigureResultService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.keyFigurePresetService = new KeyFigurePresetService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.companyTransitionBalanceRepository = makeMonitorRepository({
		model: CompanyTransitionBalance,
		route: "/Accounting2/Company/#companyId/CompanyTransitionBalance/#periodId",
		defaultFilters: { companyId: session.getCompany().getLegacyId() },
		antiForgeryToken,
	});

	my.businessOverview = new BusinessOverviewWidget({
		currentPeriod: session.getPeriod(),
	});

	my.keyFigureOverview = keyFigureOverviewWidget();
	my.companyOverview = companyOverviewWidget();

	my.topListsService = new TopListsService({
		keyFigureIds: TopListsOverviewWidget.keyFigureIds(),
		keyFigurePresetService: my.keyFigurePresetService,
		keyFigureResultService: my.keyFigureResultService,
	});

	my.topListsOverviewWidget = new TopListsOverviewWidget({
		getPeriodType: () =>
			polyglot.translateServerString(session.getPeriod().getPeriodType()),
	});

	that.show = function () {
		that.update();
	};

	that.isActionsMenuMobileOnly = function () {
		return true;
	};

	that.showCurrentYear = async function () {
		const period = session.getPeriod();

		const [annualTrendData, companyTransitionBalance] = await Promise.all([
			my.annualTrendRepository.get({
				filter: { fiscalYearId: session.getFiscalYearId() },
			}),
			my.companyTransitionBalanceRepository.get({
				filter: { periodId: period.toString() },
			}),
		]);

		my.businessOverview.show(annualTrendData, session.getPeriod());

		my.topListsService.fetch({
			period,
			callback: (...rest) => my.topListsOverviewWidget.show(...rest),
		});

		my.keyFigurePresetService
			.get("Overview")
			.then((preset) => {
				const keyFigures = preset.getKeyFigures().splice(0, 3);

				return my.keyFigureResultService.getValuesForPeriods({
					periods: [period],
					keyFigures,
				});
			})
			.then((result) => {
				my.keyFigureOverview.show(result.getValues());
			});

		my.companyOverview.show(companyTransitionBalance);
	};

	my.renderContentOn = function (html) {
		my.renderBusinessOverviewOn(html);
		my.renderCompanyOverviewOn(html);
		my.renderKeyFiguresOn(html);
		my.renderTopListOn(html);
	};

	my.renderBusinessOverviewOn = function (html) {
		html.render(
			my.section({
				id: "business-chart",
				dataHelpName: "business-chart",
				title: _("Business"),
				content: [my.businessOverview, my.renderOverviewQuickLinksOn],
			}),
		);
	};

	my.renderCompanyOverviewOn = function (html) {
		html.render(
			my.section({
				id: "companyoverview-chart",
				dataHelpName: "company-overview-chart",
				title: _("The Company"),
				content: [my.companyOverview, my.renderCompanyQuickLinksOn],
			}),
		);
	};

	my.renderKeyFiguresOn = function (html) {
		html.render(
			my.section({
				id: "kpi-overview",
				dataHelpName: "kpi-overview",
				title: _("KPIs"),
				content: my.keyFigureOverview,
			}),
		);
	};

	my.renderOverviewQuickLinksOn = function (html) {
		html.div(
			{
				class: "overview-quicklinks",
				id: "overview-business-quicklinks",
			},
			html.ul(
				{ "data-help": "overview-quicklinks" },
				html.li(
					html.a(
						{ href: my.linkTo("business-trend", {}) },
						iconAndText(
							{ space: none, icon: compareIcon },
							_("Business trend"),
						),
					),
				),
				html.li(
					html.a(
						{ href: my.linkTo("business-activity", {}) },
						iconAndText(
							{ icon: businessActivityIcon },
							_("#business-activity-document"),
						),
					),
				),
				html.li(
					html.a(
						{ href: my.linkTo("income-statement", {}) },
						iconAndText(
							{ space: none, icon: classicReportIcon },
							_("Income statement"),
						),
					),
				),
			),
		);
	};

	my.renderCompanyQuickLinksOn = function (html) {
		html.div(
			{ class: "overview-quicklinks", id: "overview-company-quicklinks" },
			html.ul(
				html.li(
					html.a(
						{ href: my.linkTo("company", {}) },
						iconAndText({ icon: companyIcon }, _("The Company")),
					),
				),
				html.li(
					html.a(
						{ href: my.linkTo("balance-sheet", {}) },
						iconAndText(
							{ space: none, icon: classicReportIcon },
							_("Balance sheet"),
						),
					),
				),
			),
		);
	};

	my.renderTopListOn = function (html) {
		html.render(
			my.section({
				dataHelpName: "toplist",
				content: my.topListsOverviewWidget,
			}),
		);
	};

	return that;
}

export default overviewDocument;
